import { useEffect, useState } from "react";
import api, { API_BASE } from "../lib/api";
import { toast } from "sonner";
import { FilePdf, Sparkle, FloppyDisk } from "@phosphor-icons/react";
import { ProfileDocument } from "../components/ProfileDocument";

const FIELDS = [
  ["company_name", "Firmenname"], ["legal_form", "Rechtsform (z. B. GmbH, UG)"],
  ["managing_director", "Geschäftsführung"], ["founded", "Gegründet"],
  ["address", "Anschrift"], ["contact", "Kontakt (Telefon / E-Mail)"],
  ["website", "Website"], ["clients", "Auftraggeber / Kostenträger"],
  ["services", "Leistungen (mit Komma getrennt)"],
];

const AiTextField = ({ label, value, onChange, onGenerate, busy, testid }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <label className="text-xs font-medium uppercase tracking-widest text-[#5B6371]">{label}</label>
      <button type="button" onClick={onGenerate} disabled={busy}
        className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md bg-[#E6F7F7] text-[#0AA09E] hover:bg-[#0AA09E] hover:text-white transition-colors disabled:opacity-60"
        data-testid={`${testid}-ai-btn`}>
        <Sparkle size={12} weight="fill" /> {busy ? "KI schreibt …" : value ? "Neu formulieren" : "Mit KI schreiben"}
      </button>
    </div>
    <textarea rows={7} className="field text-sm leading-relaxed" value={value}
      onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-text`}
      placeholder="Text per KI erzeugen oder selbst schreiben …" />
  </div>
);

export default function CompanyProfile() {
  const [profile, setProfile] = useState(null);
  const [figures, setFigures] = useState(null);
  const [busy, setBusy] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/company-profile").then(({ data }) => { setProfile(data.profile); setFigures(data.figures); })
      .catch(() => toast.error("Profil konnte nicht geladen werden"));
  }, []);

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/company-profile", { ...profile, growth_pct: parseFloat(profile.growth_pct) || 0 });
      setFigures(data.figures);
      toast.success("Profil gespeichert");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const generate = async (section) => {
    const key = section === "intro" ? "intro_text" : "business_text";
    setBusy(section);
    set(key, "");
    try {
      const res = await fetch(`${API_BASE}/company-profile/generate`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ section, profile: { ...profile, growth_pct: parseFloat(profile.growth_pct) || 0 } }),
      });
      if (!res.ok || !res.body) throw new Error();
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let text = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        text += dec.decode(value, { stream: true });
        set(key, text);
      }
      if (text.includes("[Fehler bei der KI-Erstellung")) throw new Error();
      await api.put("/company-profile", { ...profile, [key]: text, growth_pct: parseFloat(profile.growth_pct) || 0 });
      toast.success("Text erstellt und gespeichert – Sie können ihn jederzeit anpassen");
    } catch {
      set(key, profile[key] || "");
      toast.error("KI-Text konnte nicht erstellt werden – bitte später erneut versuchen");
    } finally {
      setBusy("");
    }
  };

  if (!profile || !figures) return <div className="p-10 text-[#5B6371]">Lade Profil …</div>;

  return (
    <div className="space-y-8" data-testid="company-profile-page">
      <div className="print-hide flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow">Für Leasing- & Bankpartner</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#1F242E] mt-1">Unternehmensprofil</h1>
          <p className="text-sm text-[#5B6371] mt-2 max-w-2xl">Jahresumsatz und Prognose werden automatisch aus Ihren Daten berechnet – ohne einzelne Kunden oder Ausgaben.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 border border-[#1F242E] text-[#1F242E] hover:bg-[#1F242E] hover:text-white text-sm font-medium rounded-md px-4 py-2.5 transition-colors"
            data-testid="profile-save-btn"><FloppyDisk size={16} /> {saving ? "Speichere …" : "Speichern"}</button>
          <button type="button" onClick={() => window.print()}
            className="inline-flex items-center gap-2 bg-[#FF6B5C] hover:bg-[#e85a4c] text-white text-sm font-medium rounded-md px-4 py-2.5 transition-colors"
            data-testid="profile-pdf-btn"><FilePdf size={16} /> Als PDF herunterladen</button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-8 items-start">
        <div className="print-hide bg-white border border-[#E5E7EB] rounded-lg p-5 space-y-4" data-testid="profile-editor">
          {FIELDS.map(([k, label]) => (
            <div key={k}>
              <label className="text-xs font-medium uppercase tracking-widest text-[#5B6371]">{label}</label>
              <input className="field mt-1" value={profile[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`profile-field-${k}`} />
            </div>
          ))}
          <div>
            <label className="text-xs font-medium uppercase tracking-widest text-[#5B6371]">Wachstum für Prognose {figures.next_year} (%)</label>
            <input type="number" step="1" className="field mt-1" value={profile.growth_pct}
              onChange={(e) => set("growth_pct", e.target.value)} data-testid="profile-growth-input" />
            <div className="text-[10px] text-[#94A3B8] mt-1">Nach Ändern auf „Speichern" klicken, um die Prognose neu zu berechnen.</div>
          </div>
          <AiTextField label="Wer wir sind" value={profile.intro_text} onChange={(v) => set("intro_text", v)}
            onGenerate={() => generate("intro")} busy={busy === "intro"} testid="profile-intro" />
          <AiTextField label="So verdienen wir Geld" value={profile.business_text} onChange={(v) => set("business_text", v)}
            onGenerate={() => generate("business")} busy={busy === "business"} testid="profile-business" />
        </div>

        <ProfileDocument profile={profile} figures={figures} />
      </div>
    </div>
  );
}
